import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, MapPin, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrendingVenuesPage = () => {
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchTrendingVenues();
  }, []);

  const fetchTrendingVenues = async () => {
    try {
      const response = await axios.get(`${API_URL}/venues/trending`);
      setVenues(response.data || []);
    } catch (error) {
      console.error('Failed to fetch trending venues');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FDFCF8]/95 backdrop-blur-sm border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-serif text-xl font-medium text-stone-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-amber-600" />
              Most Searched Venues
            </h1>
            <p className="text-sm text-stone-500">{venues.length} venues tracked</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-stone-300 border-t-stone-900 rounded-full animate-spin mx-auto"></div>
          </div>
        ) : venues.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-stone-200">
            <TrendingUp className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-stone-700 mb-2">No trending venues yet</h3>
            <p className="text-stone-500 mb-6">Start searching for venues to see them here</p>
            <Button onClick={() => navigate('/')} className="bg-stone-900 hover:bg-stone-800">
              Search Venues
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Stats Header */}
            <div className="bg-white border border-stone-200 rounded-xl p-6 mb-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-3xl font-bold text-stone-900">{venues.length}</p>
                  <p className="text-sm text-stone-500">Total Venues</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-amber-600">
                    {venues.reduce((acc, v) => acc + v.search_count, 0)}
                  </p>
                  <p className="text-sm text-stone-500">Total Searches</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-stone-900">
                    {venues.length > 0 ? venues[0].name.substring(0, 15) + (venues[0].name.length > 15 ? '...' : '') : '-'}
                  </p>
                  <p className="text-sm text-stone-500">Most Popular</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-stone-900">
                    {venues.length > 0 ? venues[0].search_count : 0}
                  </p>
                  <p className="text-sm text-stone-500">Top Search Count</p>
                </div>
              </div>
            </div>

            {/* Venues List */}
            <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-stone-200 bg-stone-50">
                <h3 className="font-medium text-stone-900">All Trending Venues</h3>
                <p className="text-sm text-stone-500">Sorted by popularity</p>
              </div>
              <div className="divide-y divide-stone-100">
                {venues.map((venue, idx) => (
                  <div 
                    key={idx} 
                    className="px-6 py-4 hover:bg-stone-50 transition-colors flex items-center justify-between"
                    data-testid={`trending-venue-${idx}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 bg-stone-100 rounded-full flex items-center justify-center text-sm font-medium text-stone-600">
                        {idx + 1}
                      </div>
                      <div>
                        <h4 className="font-medium text-stone-900">{venue.name}</h4>
                        {venue.location && (
                          <p className="text-sm text-stone-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {venue.location}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-lg font-bold text-amber-600">{venue.search_count}</p>
                        <p className="text-xs text-stone-500">searches</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/', { state: { searchVenue: venue.name } })}
                        className="border-stone-200"
                        data-testid={`search-venue-${idx}`}
                      >
                        <Search className="w-4 h-4 mr-1" />
                        View Report
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default TrendingVenuesPage;
